/**
 * Hero Momentum Scrolling
 * Creates a weighted scroll resistance effect on hero sections
 * Maximum resistance at y=0, gradually decreasing as user scrolls down
 */

class HeroMomentum {
  constructor(heroElement) {
    this.hero = heroElement;
    this.enabled = this.hero.dataset.momentumEnabled === 'true';

    if (!this.enabled) return;

    const strength = this.hero.dataset.momentumStrength || 'medium';

    // Resistance multipliers: higher value = slower scroll at top
    const resistanceMap = {
      mild: 0.40,    // Scroll at 60% speed at top
      medium: 0.60,  // Scroll at 40% speed at top
      strong: 0.80   // Scroll at 20% speed at top
    };

    this.resistance = resistanceMap[strength] || resistanceMap.medium;
    this.heroHeight = 0;
    this.touchStartY = null;
    this.touchStartScrollY = null;

    this.init();
  }

  init() {
    this.updateHeroHeight();
    this.bindEvents();
  }

  updateHeroHeight() {
    this.heroHeight = this.hero.offsetHeight;
  }

  bindEvents() {
    // Wheel events for desktop
    this.wheelHandler = this.handleWheel.bind(this);
    window.addEventListener('wheel', this.wheelHandler, { passive: false });

    // Touch events for mobile
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchMoveHandler = this.handleTouchMove.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);

    window.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    window.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    window.addEventListener('touchend', this.touchEndHandler, { passive: true });

    // Update height on resize
    this.resizeHandler = () => this.updateHeroHeight();
    window.addEventListener('resize', this.resizeHandler);
  }

  handleWheel(e) {
    const scrollY = window.scrollY;

    // Only apply resistance when within hero section and scrolling down
    if (scrollY >= this.heroHeight || e.deltaY <= 0) return;

    // Calculate resistance factor (1 at top, 0 at bottom of hero)
    const progressThroughHero = scrollY / this.heroHeight;
    const resistanceFactor = (1 - progressThroughHero) * this.resistance;

    if (resistanceFactor <= 0.01) return; // Skip if resistance is negligible

    e.preventDefault();

    // Apply damped scroll: higher resistance = slower scroll
    const scrollMultiplier = 1 - resistanceFactor;
    const dampedDelta = e.deltaY * scrollMultiplier;

    window.scrollBy({
      top: dampedDelta,
      behavior: 'instant'
    });
  }

  handleTouchStart(e) {
    this.touchStartY = e.touches[0].clientY;
    this.touchStartScrollY = window.scrollY;
  }

  handleTouchMove(e) {
    if (this.touchStartY === null) return;

    const scrollY = window.scrollY;
    const touchDelta = this.touchStartY - e.touches[0].clientY;

    // Only apply resistance when within hero section and scrolling down
    if (scrollY >= this.heroHeight || touchDelta <= 0) return;

    const progressThroughHero = scrollY / this.heroHeight;
    const resistanceFactor = (1 - progressThroughHero) * this.resistance;

    if (resistanceFactor <= 0.01) return;

    e.preventDefault();

    // Apply resistance to touch scroll
    const scrollMultiplier = 1 - resistanceFactor;
    const dampedDelta = touchDelta * scrollMultiplier;

    window.scrollTo({
      top: this.touchStartScrollY + dampedDelta,
      behavior: 'instant'
    });
  }

  handleTouchEnd() {
    this.touchStartY = null;
    this.touchStartScrollY = null;
  }

  destroy() {
    window.removeEventListener('wheel', this.wheelHandler);
    window.removeEventListener('touchstart', this.touchStartHandler);
    window.removeEventListener('touchmove', this.touchMoveHandler);
    window.removeEventListener('touchend', this.touchEndHandler);
    window.removeEventListener('resize', this.resizeHandler);
  }
}

// Initialize all hero sections with momentum enabled
document.addEventListener('DOMContentLoaded', () => {
  const heroSections = document.querySelectorAll('[data-momentum-enabled="true"]');
  heroSections.forEach(hero => new HeroMomentum(hero));
});

// Handle Shopify theme editor
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const hero = event.target.querySelector('[data-momentum-enabled="true"]');
    if (hero) new HeroMomentum(hero);
  });
}
